import React from "react";
import PlusIcon from "@heroicons/react/24/outline/PlusIcon";
import {
    Box,
    Stack,
    SvgIcon,
} from "@mui/material";
import { useSelection } from "../../hooks/use-selection";
import { CustomTable } from "../../components/custom-table";
import { CustomSearch } from "../../components/custom-search";
import { PageHeader } from "../../components/page-header";
import { expensesHeadCells } from "../../seed/table-headers";
import { CREATE, ROWSPERPAGE, UPDATE } from "../../utils/constant";
import { authPostRequest } from "../../services/api-service";
import {
    createVendorExpensesUrl,
    getAllVendorExpensesByPaginationUrl,
} from "../../seed/url";
import { FormDialog } from "../../components/form-dialog";
import { expensesFormFields } from "../../seed/form-fields";
import { useAuth } from "../../hooks/use-auth";

const useExpensesIds = (expenses) => {
    return React.useMemo(() => {
        return expenses.map((customer) => customer.id);
    }, [expenses]);
};

function Expenses() {
    const auth = useAuth();
    const [action, setAction] = React.useState(CREATE);
    const [rowsPerPage, setRowsPerPage] = React.useState(ROWSPERPAGE);
    const [expenses, setExpenses] = React.useState({
        page: 1,
        total_results: 0,
        total_pages: 0,
        results: [],
    });
    const [selectedData, setSelectedData] = React.useState({});
    const [searchTerm, setSearchTerm] = React.useState("");
    const [isLoading, setIsLoading] = React.useState(true);
    const administratorsIds = useExpensesIds(expenses.results);
    const adminSelection = useSelection(administratorsIds);
    const [openCreateDialog, setOpenCreateDialog] = React.useState(false);
    const values = [
        {
            id: action === UPDATE ? selectedData.id : 0,
            title: action === UPDATE ? selectedData.title : "",
            description: action === UPDATE ? selectedData.description : "",
            amount: action === UPDATE ? selectedData.amount : "",
            product_vendor_id: action === UPDATE ? selectedData.product_vendor_id : "",
            user_id: auth?.user?.id
        },
    ];
    const [order, setOrder] = React.useState('desc');
    const [orderBy, setOrderBy] = React.useState('id');

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const fetcher = React.useCallback(
        (page) => {
            setIsLoading(true);
            authPostRequest(
                getAllVendorExpensesByPaginationUrl,
                {
                    query: searchTerm,
                    sort: orderBy + " " + order,
                    limit: rowsPerPage,
                    page: page,
                },
                (data) => {
                    setExpenses(data);
                    setIsLoading(false);
                },
                (error) => {
                    setExpenses({
                        page: 1,
                        total_results: 0,
                        total_pages: 0,
                        results: [],
                    });
                    setIsLoading(false);
                }
            );
        },
        [rowsPerPage, searchTerm, orderBy, order]
    );

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
    };

    React.useEffect(() => {
        fetcher(1);
    }, [fetcher]);

    const handlePageChange = React.useCallback(
        (event, value) => {
            fetcher(value + 1);
        },
        [fetcher]
    );

    const handleRowsPerPageChange = React.useCallback((event) => {
        setRowsPerPage(event.target.value);
    }, []);

    const handleClickOpenCreateDialog = () => {
        setOpenCreateDialog(true);
    };

    const handleCloseCreateDialog = () => {
        action === UPDATE ? fetcher(expenses.page) : fetcher(1);
        setOpenCreateDialog(false);
        setAction(CREATE);
    };

    const onSelect = (data, openDialog) => {
        setSelectedData(data);
    };

    const contentPopoverItems = [];

    return (
        <>
            {openCreateDialog && (
                <FormDialog
                    open={openCreateDialog}
                    handleClose={handleCloseCreateDialog}
                    dialogTitle={"Expense"}
                    action={action}
                    fields={expensesFormFields}
                    values={values}
                    url={createVendorExpensesUrl}
                />
            )}
            <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
                <Stack spacing={2}>
                    <PageHeader
                      title="Expenses"
                      actions={[
                        {
                          label: "Add",
                          icon: <SvgIcon fontSize="small"><PlusIcon /></SvgIcon>,
                          onClick: handleClickOpenCreateDialog,
                        },
                      ]}
                    />
                    <CustomSearch handleSearch={handleSearch} />
                    <CustomTable
                        order={order}
                        orderBy={orderBy}
                        onRequestSort={handleRequestSort}
                        count={expenses.total_results}
                        items={expenses.results}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        onSelectOne={adminSelection.handleSelectOne}
                        onSelect={onSelect}
                        page={
                            expenses.page >= 1
                                ? expenses.page - 1
                                : expenses.page
                        }
                        rowsPerPage={rowsPerPage}
                        selected={adminSelection.selected}
                        headCells={expensesHeadCells}
                        popoverItems={contentPopoverItems}
                        isLoading={isLoading}
                    />
                </Stack>
            </Box>
        </>
    );
}

export default Expenses;
